import type { LDContext } from '@launchdarkly/js-client-sdk';

import { createConstantFunction } from './internal/createConstantFunction';

export const productAnalyticsDataServiceUrl = createConstantFunction<string>('productAnalyticsDataServiceUrl');
export const productAnalyticsBlockServiceUrl = createConstantFunction<string>('productAnalyticsBlockServiceUrl');
export const accelerateBaseUri = createConstantFunction<string>('accelerateBaseUri');
export const accountId = createConstantFunction<string>('accountId', { optional: true });
export const docsAlgoliaApiKey = createConstantFunction<string>('docsAlgoliaApiKey');
export const docsAlgoliaAppId = createConstantFunction<string>('docsAlgoliaAppId');
export const fernAlgoliaApiKey = createConstantFunction<string>('fernAlgoliaApiKey');
export const fernAlgoliaAppId = createConstantFunction<string>('fernAlgoliaAppId');
export const allClientSideFlags = createConstantFunction<{ [key: string]: unknown }>('allClientSideFlags');
export const assetBase = createConstantFunction<string>('assetBase');
export const assetPath = createConstantFunction<string>('assetPath');
export const backendVersion = createConstantFunction<string>('backendVersion');
export const baseUri = createConstantFunction<string>('baseUri');
export const canduClientToken = createConstantFunction<string>('canduClientToken');
export const clientSideId = createConstantFunction<string>('clientSideId');
export const cooloffRetrialEligible = createConstantFunction<boolean>('cooloffRetrialEligible', { optional: true });
export const courierBrandId = createConstantFunction<string>('courierBrandId', { optional: true });
export const courierClientKey = createConstantFunction<string>('courierClientKey', { optional: true });
export const datadogEnvironment = createConstantFunction<string>('datadogEnvironment');
export const datadogRUMApplicationId = createConstantFunction<string>('datadogRUMApplicationId');
export const datadogRUMClientToken = createConstantFunction<string>('datadogRUMClientToken');
export const datadogSite = createConstantFunction<string>('datadogSite');
export const newRelicRUMLicenseKey = createConstantFunction<string>('newRelicRUMLicenseKey', { optional: true });
export const newRelicRUMApplicationId = createConstantFunction<string>('newRelicRUMApplicationId', { optional: true });
export const newRelicRUMAccountId = createConstantFunction<string>('newRelicRUMAccountId', { optional: true });
export const newRelicRUMAgentId = createConstantFunction<string>('newRelicRUMAgentId', { optional: true });
export const newRelicRUMTrustKey = createConstantFunction<string>('newRelicRUMTrustKey', { optional: true });
export const newRelicRUMBeacon = createConstantFunction<string>('newRelicRUMBeacon', { optional: true });
export const disallowSignups = createConstantFunction<boolean>('disallowSignups');
export const documentationUri = createConstantFunction<string>('documentationUri');
export const dogfoodBaseUri = createConstantFunction<string>('dogfoodBaseUri');
export const dogfoodClientSideEventsUri = createConstantFunction<string>('dogfoodClientSideEventsUri');
export const dogfoodContext = createConstantFunction<LDContext>('dogfoodContext');
export const dogfoodSendEvents = createConstantFunction<boolean>('dogfoodSendEvents');
export const dogfoodStreamUri = createConstantFunction<string>('dogfoodStreamUri');
export const dogFoodEventsCapacity = createConstantFunction<number>('dogfoodEventsCapacity', { optional: true });
export const enableFullstoryFlagVarsIntegration = createConstantFunction<boolean>('enableFullstoryFlagVarsIntegration');
export const enableObservabilityTrackIntegration = createConstantFunction<boolean>(
  'enableObservabilityTrackIntegration',
  { optional: true },
);
export const observabilityPublicGraphUrl = createConstantFunction<string>('observabilityPublicGraphUrl', {
  optional: true,
});
export const observabilityPrivateGraphUrl = createConstantFunction<string>('observabilityPrivateGraphUrl', {
  optional: true,
});
export const observabilityOTELUrl = createConstantFunction<string>('observabilityOtelUrl', {
  optional: true,
});
export const observabilityProjectID = createConstantFunction<string>('observabilityProjectID', {
  optional: true,
});
export const federalCssStylesEnabled = createConstantFunction<boolean>('federalCssStylesEnabled');
export const euCssStylesEnabled = createConstantFunction<boolean>('euCssStylesEnabled', { optional: true });
export const frontendDatadogFlagRumTracking = createConstantFunction<boolean>('frontendDatadogFlagRumTracking');
export const frontendVersion = createConstantFunction<string>('frontendVersion');
export const ghosting = createConstantFunction<boolean>('ghosting', { optional: true });
export const googleCaptchaSiteKey = createConstantFunction<string>('googleCaptchaSiteKey');
export const googleOauthClientId = createConstantFunction<string>('googleOauthClientId', { optional: true });
export const hasLegacyPlan = createConstantFunction<boolean>('hasLegacyPlan', { optional: true });
export const hasMultipleOrganizations = createConstantFunction<boolean>('hasMultipleOrganizations', { optional: true });
export const intercomFinAppAppId = createConstantFunction<string>('intercomFinAppAppId', { optional: true });
export const intercomFinAppUserIdentityHash = createConstantFunction<string>('intercomFinAppUserIdentityHash', {
  optional: true,
});
export const isAccountLapsed = createConstantFunction<boolean>('isAccountLapsed', { optional: true });
export const isManagedInstance = createConstantFunction<boolean>('isManagedInstance');
export const isTrialAccount = createConstantFunction<boolean>('isTrialAccount', { optional: true });
export const minPasswordLength = createConstantFunction<number>('minPasswordLength');
export const passwordMinCharsPerClass = createConstantFunction<number>('passwordMinCharsPerClass');
export const passwordMinClasses = createConstantFunction<number>('passwordMinClasses');
export const planInfo = createConstantFunction<string>('planInfo', { optional: true });
/**
 * The project navigation mode the member last worked in, already collapsed to one
 * they can reach: agent mode only survives when the account has AI access. See
 * `projectNavMode` in `internal/api_internal/config_endpoints/config_get.go`.
 *
 * Optional because a build serving an older config response won't send it.
 */
export const projectNavMode = createConstantFunction<'code' | 'agent'>('projectNavMode', { optional: true });
export const secureModeContextHash = createConstantFunction<string>('secureModeContextHash');
export const slackAppHost = createConstantFunction<string>('slackAppHost');
export const slackAppId = createConstantFunction<string>('slackAppId');
export const subscriptionState = createConstantFunction<string>('subscriptionState', { optional: true });
// True when the signup behind this member was classified junk and browser analytics should stay silent.
export const suppressProductAnalytics = createConstantFunction<boolean>('suppressProductAnalytics', {
  optional: true,
});
// True when pre-auth suppression is currently enabled, so the marker cookie outlives neither
// a flag rollback nor the enforce mode it depends on. Anonymous config only.
export const suppressProductAnalyticsEnabled = createConstantFunction<boolean>('suppressProductAnalyticsEnabled', {
  optional: true,
});
export const userAvatarUrl = createConstantFunction<string>('userAvatarUrl', { optional: true });
export const userEmail = createConstantFunction<string>('userEmail', { optional: true });
export const userId = createConstantFunction<string>('userId', { optional: true });
export const welcomeSurveyCompleted = createConstantFunction<boolean>('welcomeSurveyCompleted', { optional: true });

/** True only when authenticated config explicitly reports the welcome survey is incomplete. */
export function mustCompleteWelcomeSurvey(): boolean {
  return welcomeSurveyCompleted() === false;
}

export const ldvov = createConstantFunction<string>('ldvov', { optional: true });
export const stripePublishableKey = createConstantFunction<string>('stripePublishableKey', { optional: true });
export const segmentWriteKey = createConstantFunction<string>('segmentWriteKey');
/** Signup attribution resolved from the captured cookie. Anonymous config only. */
export const signupReferer = createConstantFunction<string>('signupReferer', { optional: true });
export const uberfunnelTrackingId = createConstantFunction<string>('uberfunnelTrackingId');
export const identifyTraits = createConstantFunction<{ [key: string]: unknown }>('identifyTraits', { optional: true });
export const groupTraits = createConstantFunction<{ [key: string]: unknown }>('groupTraits', { optional: true });
export const enabledSSOConfig = createConstantFunction<boolean>('enabledSSOConfig', { optional: true });
export const beastModeExpiration = createConstantFunction<number>('beastModeExpiration', { optional: true });
export const hockeystackApiKey = createConstantFunction<string>('hockeystackApiKey', { optional: true });
export const redshiftIntermediaryRoleArn = createConstantFunction<string>('redshiftIntermediaryRoleArn', {
  optional: true,
});

// sandbox
export const sandboxBaseUri = createConstantFunction<string>('sandboxBaseUri');
export const sandboxVisitorAccountId = createConstantFunction<string>('sandboxVisitorAccountID');
export const sandboxVisitorMemberId = createConstantFunction<string>('sandboxVisitorMemberID');
export const sandboxVisitorBaseUri = createConstantFunction<string>('sandboxVisitorBaseUri');

/*
Use isSandbox to customize UI behavior in the sandbox.
For example hiding sdk keys or showing the sandbox banner.

 isSandbox is true if we are in a sandbox session:
 window.location.host === demo.app.launchdarkly.com // prod
 window.location.host === demo.ld-stg.launchdarkly.com // staging
 */
export const isSandbox = createConstantFunction<boolean>('isSandbox', { optional: true });

export { mockConstants } from './internal/mockConstants';
